'use client';

import { useCallback } from 'react';
import { AnimatePresence, motion, type Transition } from 'motion/react';
import { useShallow } from 'zustand/react/shallow';
import BulbIcon from '../../icons/react/icon-bulb';
import XLargeIcon from '../../icons/react/icon-x-large';
import { HorizontalStack } from '../alignment/horizontal-stack';
import { VerticalStack } from '../alignment/vertical-stack';
import { Button } from '../button/button';
import { List } from '../list/list';
import { Typography } from '../typography';
import { useRecommendationNag } from './state';
import styles from './recommendation-nag.module.scss';

const ANIMATION_ITEM_TRANSITION = { duration: 0.25, ease: 'easeOut' } as const satisfies Transition;
const ANIMATION_ITEM_INITIAL = { transform: 'translateY(100%)' };
const ANIMATION_ITEM_FINAL = { transform: 'translateY(0)' };

export function RecommendationNagRail() {
  const nags = useRecommendationNag(useShallow((state) => state.recommendationNags));
  const removeNag = useRecommendationNag(useShallow((state) => state.removeRecommendationNag));
  const activeNag = nags[0];
  const handleClose = useCallback(() => {
    if (activeNag?.id) {
      removeNag(activeNag.id);
    }
  }, [removeNag, activeNag?.id]);

  return (
    <motion.div className={styles.recommendationNagRail}>
      <AnimatePresence>
        {activeNag ? (
          <motion.div
            animate={ANIMATION_ITEM_FINAL}
            className={styles.recommendationNag}
            initial={ANIMATION_ITEM_INITIAL}
            key={activeNag.id}
            transition={ANIMATION_ITEM_TRANSITION}
          >
            <VerticalStack gap={4}>
              <HorizontalStack align="space-between" blockAlign="center" gap={2} wrap="nowrap">
                <HorizontalStack blockAlign="center" gap={2} wrap="nowrap">
                  <div className={styles.icon}>
                    <BulbIcon aria-hidden height={16} />
                  </div>

                  <Typography.Level3>{activeNag.title || 'Suggestions'}</Typography.Level3>
                </HorizontalStack>

                <Button
                  affixIcon={<XLargeIcon aria-hidden />}
                  aria-label="Close recommendations"
                  onClick={handleClose}
                  size="small"
                  variant="secondary"
                />
              </HorizontalStack>

              <List gap={2} items={activeNag.items} />
            </VerticalStack>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </motion.div>
  );
}
